import { Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useAdminPlaybook,
  type AdminPlaybookDetail as DetailRow,
} from "@/queries/adminPlaybooks";

interface Props {
  id: string;
}

export function PlaybookDetail({ id }: Props) {
  const { t } = useTranslation("admin-playbooks");
  const { data, isPending, isError } = useAdminPlaybook(id);

  if (isPending) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">{t("page.loading")}</p>
      </Shell>
    );
  }
  if (isError || !data) {
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertDescription>{t("page.loadError")}</AlertDescription>
        </Alert>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">
          {t("detail.title", { name: data.display_name })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("detail.subtitle", {
            version: data.schema_version,
            category: data.category,
          })}
        </p>
      </header>

      <Alert>
        <AlertDescription>{t("detail.yamlSourceNote")}</AlertDescription>
      </Alert>

      <DetailBody data={data} />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation("admin-playbooks");
  return (
    <div className="flex flex-col gap-6 p-6">
      <Button asChild variant="ghost" size="sm" className="w-fit">
        <Link to="/admin/playbooks">
          <ChevronLeft className="h-4 w-4" /> {t("detail.backToList")}
        </Link>
      </Button>
      {children}
    </div>
  );
}

interface ReactionCondition {
  field: string;
  op: string;
  value: unknown;
}

interface ExpectedReaction {
  timeframe: string;
  direction: string;
  assets?: string[];
  rationale?: string;
  sizing_hint?: string;
  conditions?: ReactionCondition[];
}

interface ConfidenceModifier {
  condition: string;
  threshold?: number | null;
  value?: string | null;
  delta: number;
}

interface KillCondition {
  description: string;
  detection: string;
  keywords?: string[] | null;
  window_hours?: number | null;
  pct?: number | null;
}

interface HistoricalPrecedent {
  event_id: string;
  note: string;
}

interface OperationalConfig {
  signal_half_life_hours: number;
  min_confidence_for_paper: number;
  min_confidence_for_live: number;
  cooldown_hours_after_loss: number;
  max_concurrent_hypotheses: number;
  notes_for_operator?: string | null;
}

interface BacktestConfig {
  historical_window_start: string;
  expected_min_hit_rate: number;
  expected_min_avg_pnl_pct: number;
}

interface Triggers {
  required_signal_kinds: string[];
  match_conditions: Array<Record<string, unknown>>;
  confluence_window_hours: number;
  min_independent_source_types: number;
  event_calendar?: Record<string, unknown> | null;
}

interface YamlShape {
  description?: string;
  triggers?: Triggers;
  expected_reactions?: ExpectedReaction[];
  confidence_modifiers?: ConfidenceModifier[];
  kill_conditions?: KillCondition[];
  historical_precedents?: HistoricalPrecedent[];
  operational?: OperationalConfig;
  backtest?: BacktestConfig | null;
  risk_classification?: Record<string, unknown> | null;
  magnitude_thresholds?: Record<string, unknown> | null;
  regime_context?: Record<string, unknown> | null;
}

function DetailBody({ data }: { data: DetailRow }) {
  const yaml = data.yaml as unknown as YamlShape;
  const { t } = useTranslation("admin-playbooks");

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("detail.description")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {yaml.description ?? "—"}
          </p>
        </CardContent>
      </Card>

      {yaml.triggers ? <TriggersCard triggers={yaml.triggers} /> : null}

      {yaml.expected_reactions?.length ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("detail.expectedReactions")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {yaml.expected_reactions.map((er, idx) => (
              <ReactionItem key={idx} er={er} />
            ))}
          </CardContent>
        </Card>
      ) : null}

      {yaml.kill_conditions?.length ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("detail.killConditions")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {yaml.kill_conditions.map((kc, idx) => (
              <div key={idx} className="rounded-md border p-3 text-sm">
                <div className="font-medium">{kc.description}</div>
                <div className="text-xs text-muted-foreground">
                  {t("detail.killConditionDetection")}: {kc.detection}
                </div>
                {kc.keywords?.length ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {kc.keywords.map((k) => (
                      <Badge key={k} variant="outline">
                        {k}
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {yaml.confidence_modifiers?.length ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("detail.confidenceModifiers")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {yaml.confidence_modifiers.map((cm, idx) => (
                <li key={idx} className="flex items-center justify-between gap-2">
                  <code className="text-xs">{cm.condition}</code>
                  <span className="tabular-nums">
                    {cm.delta >= 0 ? "+" : ""}
                    {cm.delta.toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {yaml.historical_precedents?.length ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("detail.historicalPrecedents")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {yaml.historical_precedents.map((hp) => (
              <div key={hp.event_id} className="rounded-md border p-3 text-sm">
                <div className="font-mono text-xs text-muted-foreground">
                  {hp.event_id}
                </div>
                <div>{hp.note}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {yaml.operational ? <OperationalCard op={yaml.operational} /> : null}
      {yaml.backtest ? <BacktestCard bt={yaml.backtest} /> : null}
    </div>
  );
}

function TriggersCard({ triggers }: { triggers: Triggers }) {
  const { t } = useTranslation("admin-playbooks");
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("detail.triggers")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <Field label={t("detail.triggersRequiredSignalKinds")}>
          <div className="flex flex-wrap gap-1">
            {triggers.required_signal_kinds.map((k) => (
              <Badge key={k} variant="outline">
                {k}
              </Badge>
            ))}
          </div>
        </Field>
        <Field label={t("detail.triggersConfluenceWindowHours")}>
          {triggers.confluence_window_hours}
        </Field>
        <Field label={t("detail.triggersMinIndependentSourceTypes")}>
          {triggers.min_independent_source_types}
        </Field>
        {triggers.match_conditions.length ? (
          <Field label={t("detail.triggersMatchConditions")}>
            <ul className="flex flex-col gap-1 font-mono text-xs">
              {triggers.match_conditions.map((mc, idx) => (
                <li key={idx} className="rounded bg-muted px-2 py-1">
                  {JSON.stringify(mc)}
                </li>
              ))}
            </ul>
          </Field>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ReactionItem({ er }: { er: ExpectedReaction }) {
  const { t } = useTranslation("admin-playbooks");
  return (
    <div className="rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={er.direction === "long" ? "success" : "destructive"}>
          {er.direction}
        </Badge>
        <Badge variant="outline">{er.timeframe}</Badge>
        {er.sizing_hint ? (
          <Badge variant="muted">{er.sizing_hint}</Badge>
        ) : null}
      </div>
      {er.assets?.length ? (
        <div className="mt-2">
          <span className="text-xs text-muted-foreground">
            {t("detail.expectedReactionAssets")}:{" "}
          </span>
          <span className="font-mono text-xs">{er.assets.join(", ")}</span>
        </div>
      ) : null}
      {er.rationale ? (
        <p className="mt-2 text-xs text-muted-foreground">{er.rationale}</p>
      ) : null}
      {er.conditions?.length ? (
        <div className="mt-2">
          <span className="text-xs text-muted-foreground">
            {t("detail.expectedReactionConditions")}:
          </span>
          <ul className="mt-1 flex flex-col gap-1 font-mono text-xs">
            {er.conditions.map((c, idx) => (
              <li key={idx} className="rounded bg-muted px-2 py-1">
                {c.field} {c.op} {String(c.value)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function OperationalCard({ op }: { op: OperationalConfig }) {
  const { t } = useTranslation("admin-playbooks");
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("detail.operational")}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <Field label={t("detail.operationalSignalHalfLifeHours")}>
          {op.signal_half_life_hours}
        </Field>
        <Field label={t("detail.operationalMinConfidencePaper")}>
          {op.min_confidence_for_paper}
        </Field>
        <Field label={t("detail.operationalMinConfidenceLive")}>
          {op.min_confidence_for_live}
        </Field>
        <Field label={t("detail.operationalCooldownHoursAfterLoss")}>
          {op.cooldown_hours_after_loss}
        </Field>
        <Field label={t("detail.operationalMaxConcurrentHypotheses")}>
          {op.max_concurrent_hypotheses}
        </Field>
        {op.notes_for_operator ? (
          <div className="sm:col-span-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t("detail.operationalNotesForOperator")}
            </div>
            <p className="whitespace-pre-wrap text-sm">{op.notes_for_operator}</p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function BacktestCard({ bt }: { bt: BacktestConfig }) {
  const { t } = useTranslation("admin-playbooks");
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("detail.backtest")}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
        <Field label={t("detail.backtestHistoricalWindowStart")}>
          {bt.historical_window_start}
        </Field>
        <Field label={t("detail.backtestExpectedMinHitRate")}>
          {bt.expected_min_hit_rate}
        </Field>
        <Field label={t("detail.backtestExpectedMinAvgPnlPct")}>
          {bt.expected_min_avg_pnl_pct}
        </Field>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="tabular-nums">{children}</div>
    </div>
  );
}
